// src/pages/speaking-clubs/SpeakingClubForm.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Save, Loader2, Mic, Plus, Trash2 } from "lucide-react";
import api from "../../api/axios";
import { speakingClubsAPI, flattenApiError } from "../../services/speakingClubService";

const DAYS = [
  { value: 0, label: "الاثنين" },
  { value: 1, label: "الثلاثاء" },
  { value: 2, label: "الأربعاء" },
  { value: 3, label: "الخميس" },
  { value: 4, label: "الجمعة" },
  { value: 5, label: "السبت" },
  { value: 6, label: "الأحد" },
];

const RECURRENCE = [
  { value: "weekly", label: "أسبوعي" },
  { value: "monthly", label: "شهري" },
  { value: "once", label: "مرة واحدة" },
];

const toArray = (d, key) =>
  Array.isArray(d) ? d : d?.[key] || d?.results || [];

const inputCls =
  "w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-right bg-white";

export default function SpeakingClubForm() {
  const navigate = useNavigate();
  const { clubId } = useParams();
  const isEdit = !!clubId;

  const [form, setForm] = useState({
    language: "",
    teacher: "",
    title: "",
    description: "",
    recurrence: "weekly",
    duration: "",
    max_students: 10,
    is_free: true,
    price: "",
    meeting_link: "",
    is_active: true,
    schedules: [{ day_of_week: 5, time: "18:00" }],
  });
  const [languages, setLanguages] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // المدرسين (فيهم language و language_name)
        const tRes = await api.get("/booking/teachers/");
        const teachersList = toArray(tRes.data, "teachers");
        setTeachers(teachersList);

        // اللغات: نجرب endpoint اللغات، ولو فشل نستخرجها من المدرسين
        let langs = [];
        try {
          const lRes = await api.get("/languages/");
          langs = toArray(lRes.data, "languages");
        } catch {
          const map = new Map();
          teachersList.forEach((t) => {
            if (t.language) map.set(t.language, { id: t.language, name: t.language_name });
          });
          langs = [...map.values()];
        }
        setLanguages(langs);

        if (isEdit) {
          const c = await speakingClubsAPI.getById(clubId);
          setForm({
            language: c.language,
            teacher: c.teacher,
            title: c.title || "",
            description: c.description || "",
            recurrence: c.recurrence || "weekly",
            duration: c.duration || "",
            max_students: c.max_students,
            is_free: c.is_free,
            price: c.is_free ? "" : c.price,
            meeting_link: c.meeting_link || "",
            is_active: c.is_active,
            schedules: (c.schedules || []).map((s) => ({
              day_of_week: s.day_of_week,
              time: String(s.time).slice(0, 5),
            })),
          });
        }
      } catch (err) {
        setError(flattenApiError(err.response?.data));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const filteredTeachers = teachers.filter(
    (t) => !t.language || String(t.language) === String(form.language)
  );

  const updateSchedule = (i, key, value) =>
    setForm((f) => {
      const s = [...f.schedules];
      s[i] = { ...s[i], [key]: key === "day_of_week" ? Number(value) : value };
      return { ...f, schedules: s };
    });
  const addSchedule = () =>
    setForm((f) => ({
      ...f,
      schedules: [...f.schedules, { day_of_week: 5, time: "18:00" }],
    }));
  const removeSchedule = (i) =>
    setForm((f) => ({ ...f, schedules: f.schedules.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.language) return setError("اختر اللغة");
    if (!form.teacher) return setError("اختر المدرس");
    if (!form.title.trim()) return setError("عنوان النادي مطلوب");
    if (!form.description.trim()) return setError("وصف النادي مطلوب");
    if (!form.duration.trim()) return setError("المدة مطلوبة");
    if (!form.max_students || Number(form.max_students) < 1)
      return setError("الحد الأقصى للطلاب لازم يكون 1 على الأقل");
    if (!form.is_free && !(Number(form.price) > 0))
      return setError("حدد سعر أكبر من صفر للنادي المدفوع");
    if (form.schedules.length === 0) return setError("أضف موعد واحد على الأقل");

    const payload = {
      language: Number(form.language),
      teacher: Number(form.teacher),
      title: form.title,
      description: form.description,
      recurrence: form.recurrence,
      duration: form.duration,
      max_students: Number(form.max_students),
      is_free: form.is_free,
      price: form.is_free ? 0 : Number(form.price),
      meeting_link: form.meeting_link.trim() || null,
      is_active: form.is_active,
      schedules: form.schedules.map((s) => ({
        day_of_week: s.day_of_week,
        time: s.time.length === 5 ? `${s.time}:00` : s.time,
      })),
    };

    try {
      setSaving(true);
      setError("");
      if (isEdit) await speakingClubsAPI.update(clubId, payload);
      else await speakingClubsAPI.create(payload);
      navigate("/dashboard/speaking-clubs");
    } catch (err) {
      setError(flattenApiError(err.response?.data));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/dashboard/speaking-clubs")}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
          <Mic className="w-5 h-5 text-emerald-700" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">
          {isEdit ? "تعديل نادي محادثة" : "إضافة نادي محادثة"}
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm text-right">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 text-right">
              اللغة <span className="text-red-500">*</span>
            </label>
            <select
              value={form.language}
              onChange={(e) => setForm((f) => ({ ...f, language: e.target.value, teacher: "" }))}
              className={inputCls}
            >
              <option value="">-- اختر --</option>
              {languages.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 text-right">
              المدرس <span className="text-red-500">*</span>
            </label>
            <select
              value={form.teacher}
              onChange={(e) => set("teacher", e.target.value)}
              className={inputCls}
              disabled={!form.language}
            >
              <option value="">{form.language ? "-- اختر --" : "اختر اللغة أولاً"}</option>
              {filteredTeachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            عنوان النادي <span className="text-red-500">*</span>
          </label>
          <input
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            className={inputCls}
          />
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            الوصف <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            className={`${inputCls} resize-none`}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 text-right">
              المدة <span className="text-red-500">*</span>
            </label>
            <input
              value={form.duration}
              onChange={(e) => set("duration", e.target.value)}
              placeholder="مثال: ساعة، شهر"
              className={inputCls}
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 text-right">
              نظام التكرار
            </label>
            <select
              value={form.recurrence}
              onChange={(e) => set("recurrence", e.target.value)}
              className={inputCls}
            >
              {RECURRENCE.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            الحد الأقصى لعدد الطلاب <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min={1}
            value={form.max_students}
            onChange={(e) => set("max_students", e.target.value)}
            className={inputCls}
          />
        </div>

        {/* مجاني / مدفوع */}
        <div className="space-y-3 border border-gray-200 rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-700 text-right">
            نوع النادي
          </label>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => set("is_free", false)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                !form.is_free
                  ? "bg-amber-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              مدفوع
            </button>
            <button
              type="button"
              onClick={() => set("is_free", true)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                form.is_free
                  ? "bg-emerald-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              مجاني
            </button>
          </div>
          {!form.is_free && (
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 text-right">
                السعر (ريال) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.price}
                onChange={(e) => set("price", e.target.value)}
                className={inputCls}
              />
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            رابط الجلسة (يتبعت للطالب على الإيميل بعد الحجز)
          </label>
          <input
            type="url"
            dir="ltr"
            value={form.meeting_link}
            onChange={(e) => set("meeting_link", e.target.value)}
            placeholder="https://..."
            className={`${inputCls} text-left`}
          />
        </div>

        {/* المواعيد */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={addSchedule}
              className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700 font-semibold"
            >
              <Plus className="w-4 h-4" />
              إضافة موعد
            </button>
            <label className="block text-sm font-medium text-gray-700 text-right">
              المواعيد <span className="text-red-500">*</span>
            </label>
          </div>
          {form.schedules.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => removeSchedule(i)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <input
                type="time"
                value={s.time}
                onChange={(e) => updateSchedule(i, "time", e.target.value)}
                className={`${inputCls} flex-1`}
              />
              <select
                value={s.day_of_week}
                onChange={(e) => updateSchedule(i, "day_of_week", e.target.value)}
                className={`${inputCls} flex-1`}
              >
                {DAYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3">
          <label htmlFor="club_active" className="text-sm font-medium text-gray-700">
            نشط
          </label>
          <input
            id="club_active"
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
            className="w-4 h-4 accent-emerald-600"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate("/dashboard/speaking-clubs")}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "حفظ التعديلات" : "إنشاء النادي"}
          </button>
        </div>
      </form>
    </div>
  );
}
